import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  LinearProgress,
  Chip,
  Grid,
  Paper,
  // Fade,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Badge,
  Divider,
  Slider,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  // Stop,
  VolumeUp,
  VolumeDown,
  // Headset,
  Timer,
  Spa,
  Psychology,
  FavoriteRounded,
  Star,
  // BookmarkBorder,
  // Bookmark,
  ArrowBack,
  Delete,
  AccessTime,
  // AutoAwesome,
  SelfImprovement,
  TrendingUp,
  EmojiEvents,
  MoreVert,
  BookOutlined,
  FlashOn,
  Analytics,
  Settings,
  Logout,
  MonetizationOn,
  People,
  SportsEsports,
  EmojiObjects,
  // Archive
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../config/supabase';
import { useChallengeNotification } from '../contexts/ChallengeNotificationContext';
import SecureLogger from '../utils/secureLogger';

// Function to notify backend of meditation completion
const notifyBackendOfMeditationActivity = async (session, showChallengeCompletion) => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    const response = await fetch(`${backendUrl}/api/challenges/force-update-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        activityType: 'meditation_sessions'
      })
    });

    if (response.ok) {
      const data = await response.json();
      SecureLogger.debug('✅ Meditation session progress updated');
      
      // Check multiple possible locations for challenge completion data
      const challengesCompleted = data.challengesCompleted || 
                                 data.result?.challengesCompleted || 
                                 data.result?.challenges_completed ||
                                 [];
      
      SecureLogger.debug('🔍 Meditation challenges completed:', challengesCompleted?.length || 0);
      
      // Check if any challenges were completed
      if (challengesCompleted.length > 0) {
        console.log('🎉 Meditation challenge completed! Processing...', challengesCompleted);
        
        // Update user's total XP in database for each completed challenge
        for (const completedChallenge of challengesCompleted) {
          const xpReward = completedChallenge.xp_reward || 50;
          
          try {
            // Add XP to user's total_xp in user_profiles table
            const { error: xpError } = await supabase.rpc('increment_user_xp', {
              user_id: session.user.id,
              xp_amount: xpReward
            });

            if (xpError) {
              console.error('Error updating user XP:', xpError);
              // Fallback: update directly if RPC doesn't exist
              const { data: currentProfile, error: fetchError } = await supabase
                .from('user_profiles')
                .select('total_xp')
                .eq('id', session.user.id)
                .single();

              if (!fetchError && currentProfile) {
                const { error: updateError } = await supabase
                  .from('user_profiles')
                  .update({ total_xp: (currentProfile.total_xp || 0) + xpReward })
                  .eq('id', session.user.id);

                if (updateError) {
                  console.error('Error updating XP directly:', updateError);
                } else {
                  console.log(`✅ Added ${xpReward} XP to user profile`);
                }
              }
            } else {
              console.log(`✅ Added ${xpReward} XP to user profile via RPC`);
            }
          } catch (xpUpdateError) {
            console.error('Error updating user XP:', xpUpdateError);
          }
          
          // Show modal for completed challenge
          showChallengeCompletion(
            completedChallenge.title,
            xpReward,
            'meditation'
          );
        }
      }
    } else {
      console.error('❌ Failed to update meditation session progress:', response.status);
    }
  } catch (error) {
    console.error('❌ Error notifying backend of meditation activity:', error);
  }
};

const IntelligentMeditation = () => {
  const { session } = useAuth();
  const { showChallengeCompletion } = useChallengeNotification();
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [selectedMeditation, setSelectedMeditation] = useState(null);
  // const [meditationLibrary, setMeditationLibrary] = useState([]);
  const [personalizedMeditations, setPersonalizedMeditations] = useState([]);
  // const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStage, setGenerationStage] = useState('');
  // const [generationJobId, setGenerationJobId] = useState(null);
  const [lastClickTime, setLastClickTime] = useState(0);
  // const [audioUrl, setAudioUrl] = useState('');
  const [savedMeditations, setSavedMeditations] = useState([]);
  const [meditationType, setMeditationType] = useState('stress-relief');
  // const [meditationDuration, setMeditationDuration] = useState(5);
  const [backgroundSound, setBackgroundSound] = useState('inner-peace');
  const [customInput, setCustomInput] = useState('');
  const [backgroundSounds, setBackgroundSounds] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Add placeholder setters for ESLint
  const setGenerationJobId = () => {};
  const setAudioUrl = () => {};
  const setMeditationLibrary = () => {};
  const isMenuOpen = Boolean(menuAnchorEl);

  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Meditation categories and types
  const meditationCategories = [
    { id: 'stress-relief', name: 'Stress Relief', icon: <Spa />, color: '#E3F2FD', description: 'Calm your mind and release tension' },
    { id: 'anxiety', name: 'Anxiety Management', icon: <FavoriteRounded />, color: '#F3E5F5', description: 'Find peace and overcome anxious thoughts' },
    { id: 'sleep', name: 'Sleep & Rest', icon: <Psychology />, color: '#E8F5E8', description: 'Prepare your mind and body for restful sleep' },
    { id: 'focus', name: 'Focus & Clarity', icon: <Star />, color: '#FFF3E0', description: 'Enhance concentration and mental clarity' },
    { id: 'self-compassion', name: 'Self-Compassion', icon: <FavoriteRounded />, color: '#FCE4EC', description: 'Cultivate kindness and acceptance toward yourself' },
    { id: 'confidence', name: 'Confidence Building', icon: <Star />, color: '#E1F5FE', description: 'Build inner strength and self-assurance' }
  ];

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  // Check for ongoing generation from localStorage
  const checkOngoingGeneration = useCallback(() => {
    const ongoingGeneration = localStorage.getItem('ongoingMeditation');
    if (ongoingGeneration) {
      try {
        const { jobId, startTime, category, duration, backgroundSound } = JSON.parse(ongoingGeneration);
        const elapsed = Date.now() - startTime;
        const estimatedTotal = 30000; // 30 seconds estimated total time
        
        if (elapsed < estimatedTotal) {
          setGenerating(true);
          setGenerationJobId(jobId);
          setGenerationStage('Continuing generation...');
          setGenerationProgress(Math.min((elapsed / estimatedTotal) * 100, 90));
          startProgressPolling(jobId);
        } else {
          // Check if generation completed while away
          checkGenerationStatus(jobId);
        }
      } catch (error) {
        console.error('Error parsing ongoing generation:', error);
        localStorage.removeItem('ongoingMeditation');
      }
    }
  }, []);

  // Load user profile
  const loadUserProfile = useCallback(async () => {
    try {
      if (!session?.user?.id) return;
      
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
        
      if (error) {
        console.log('Profile not found, will create one');
        return;
      }
      
      setUserProfile(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }, [session?.user?.id]);

  // Load saved meditations
  const loadSavedMeditations = useCallback(async () => {
    SecureLogger.logUserAction('Loading saved meditations', session);
    
    try {
      if (!session?.user?.id) {
        SecureLogger.debug('❌ No user session found');
        return;
      }
      
      const { data: meditations, error } = await supabase
        .from('meditation_sessions')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
        
      SecureLogger.debug('📊 Meditation query result:', { 
        hasResults: !!meditations,
        error: !!error,
        count: meditations?.length || 0,
        userAuthenticated: !!session.user.id 
      });
        
      if (error) {
        SecureLogger.error('❌ Database error loading saved meditations:', error);
        return;
      }
      
      SecureLogger.debug('✅ Successfully loaded meditations:', meditations?.length || 0);
      
      // Log the first meditation if any exist (no sensitive data)
      if (meditations && meditations.length > 0) {
        SecureLogger.debug('📝 Sample meditation data available:', {
          hasTitle: !!meditations[0].title,
          hasCategory: !!meditations[0].category,
          hasScript: !!meditations[0].script
        });
      }
      
      setSavedMeditations(meditations || []);
      
      // Filter for personalized meditations
      const personalizedMeds = meditations?.filter(m => m.is_personalized === true) || [];
      
      setPersonalizedMeditations(personalizedMeds);
      SecureLogger.debug('✅ Personalized meditations found:', personalizedMeds.length);
      
    } catch (error) {
      SecureLogger.error('❌ Error loading saved meditations:', error);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    initializeMeditationLibrary();
    loadUserProfile();
    loadSavedMeditations();
    loadBackgroundSounds();
    checkOngoingGeneration();
  }, [session?.user?.id, checkOngoingGeneration, loadSavedMeditations, loadUserProfile]);

  // Start polling for generation progress
  const startProgressPolling = (jobId) => {
    // For now, since we don't have the backend endpoint, just simulate completion
    const ongoingGeneration = localStorage.getItem('ongoingMeditation');
    if (!ongoingGeneration) return;

    try {
      const { startTime } = JSON.parse(ongoingGeneration);
      const elapsed = Date.now() - startTime;
      const estimatedTotal = 30000; // 30 seconds

      if (elapsed >= estimatedTotal) {
        // Consider it completed
        setGenerating(false);
        setGenerationProgress(0);
        setGenerationStage('');
        localStorage.removeItem('ongoingMeditation');
        return;
      }

      // Continue simulating progress
      const pollInterval = setInterval(() => {
        const currentElapsed = Date.now() - startTime;
        const progress = Math.min((currentElapsed / estimatedTotal) * 100, 100);
        
        if (progress >= 100) {
          clearInterval(pollInterval);
          setGenerating(false);
          setGenerationProgress(0);
          setGenerationStage('');
          localStorage.removeItem('ongoingMeditation');
        } else {
          setGenerationProgress(progress);
          if (progress < 30) {
            setGenerationStage('Analyzing your preferences...');
          } else if (progress < 70) {
            setGenerationStage('Creating personalized script...');
          } else if (progress < 95) {
            setGenerationStage('Generating audio with AI voice...');
          } else {
            setGenerationStage('Finalizing your meditation...');
          }
        }
      }, 1000);

      intervalRef.current = pollInterval;
    } catch (error) {
      console.error('Error in progress polling:', error);
      localStorage.removeItem('ongoingMeditation');
    }
  };

  // Check generation status from backend
  const checkGenerationStatus = async (jobId) => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/meditation/generation-status/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`
        }
      });

      if (response.ok) {
        return await response.json();
      }
      
      // If endpoint doesn't exist, simulate completion for now
      return {
        completed: true,
        meditation: null
      };
    } catch (error) {
      // Fallback: assume completed if we can't check status
      return {
        completed: true,
        meditation: null
      };
    }
  };

  // Update progress display
  const updateProgress = (stage, progress) => {
    setGenerationStage(stage);
    setGenerationProgress(progress);
  };

  // Handle generation completion
  const handleGenerationComplete = (meditation) => {
    setGenerating(false);
    setGenerationProgress(100);
    setGenerationStage('Complete!');
    localStorage.removeItem('ongoingMeditation');
    
    // Add meditation to list
    const personalizedMeditation = {
      id: meditation.id,
      title: meditation.title,
      category: meditation.category,
      duration: meditation.duration,
      description: `Personalized ${meditation.category} meditation (${meditation.duration} minutes)`,
      script: meditation.script,
      audio_url: meditation.audio_url,
      type: 'personalized',
      isPersonalized: true
    };

    setPersonalizedMeditations(prev => [personalizedMeditation, ...prev]);
    setSelectedMeditation(personalizedMeditation);
    
    if (meditation.audio_url) {
      setAudioUrl(meditation.audio_url);
    }

    // Update user profile with new token balance if available
    if (meditation.tokensRemaining !== undefined) {
      setUserProfile(prev => prev ? { ...prev, token_balance: meditation.tokensRemaining } : prev);
    }

    // Reset progress after a short delay and scroll to the new meditation
    setTimeout(() => {
      setGenerationProgress(0);
      setGenerationStage('');
      
      // Scroll to the generated meditations section
      const meditationsSection = document.getElementById('generated-meditations');
      if (meditationsSection) {
        meditationsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 1500);
  };

  const initializeMeditationLibrary = () => {
    // Pre-built meditation library
    const library = [
      {
        id: 1,
        title: "Mindful Breathing for Anxiety",
        category: "anxiety",
        duration: 5,
        description: "Gentle breathing exercises to calm anxiety and center your mind",
        type: "guided",
        difficulty: "beginner"
      },
      {
        id: 2,
        title: "Deep Sleep Relaxation",
        category: "sleep",
        duration: 10,
        description: "Progressive muscle relaxation to prepare for restful sleep",
        type: "guided",
        difficulty: "intermediate"
      },
      {
        id: 3,
        title: "Stress Release Body Scan",
        category: "stress-relief",
        duration: 8,
        description: "Full body awareness practice to release tension and stress",
        type: "guided",
        difficulty: "beginner"
      },
      {
        id: 4,
        title: "Focus Enhancement",
        category: "focus",
        duration: 7,
        description: "Concentration meditation to improve mental clarity",
        type: "guided",
        difficulty: "intermediate"
      }
    ];
    setMeditationLibrary(library);
  };

  const loadBackgroundSounds = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/meditation/background-sounds`);
      
      if (!response.ok) {
        console.error('Failed to load background sounds');
        return;
      }
      
      const data = await response.json();
      setBackgroundSounds(data.backgroundSounds || []);
    } catch (error) {
      console.error('Error loading background sounds:', error);
    }
  };

  // Get max duration for users
  const getMaxDuration = () => {
    return 5; // All users can create up to 5-minute meditations
  };

  // Protected meditation generation to prevent double-clicking
  const handleGenerateClick = () => {
    const currentTime = Date.now();
    const timeSinceLastClick = currentTime - lastClickTime;
    
    // Prevent double-clicking within 1 second
    if (timeSinceLastClick < 1000) {
      console.log('Double-click prevented');
      return;
    }
    
    // Don't allow new generation if already generating
    if (generating) {
      console.log('Generation already in progress');
      return;
    }
    
    // Check if there's already an ongoing generation in localStorage
    const ongoingGeneration = localStorage.getItem('ongoingMeditation');
    if (ongoingGeneration) {
      console.log('Previous generation still in progress');
      return;
    }
    
    setLastClickTime(currentTime);
    generatePersonalizedMeditation(meditationType, 5);
  };

  const generatePersonalizedMeditation = async (category, duration = 10) => {
    setGenerating(true);
    setError('');
    setGenerationProgress(0);
    
    // Generate a unique job ID
    const jobId = `meditation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setGenerationJobId(jobId);
    
    // Store generation info in localStorage for persistence
    localStorage.setItem('ongoingMeditation', JSON.stringify({
      jobId,
      startTime: Date.now(),
      category,
      duration,
      backgroundSound
    }));
    
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        setError('Please log in to generate personalized meditations');
        setGenerating(false);
        localStorage.removeItem('ongoingMeditation');
        return;
      }

      // Simulate progress stages
      setGenerationStage('Analyzing your preferences...');
      setGenerationProgress(10);

      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setGenerationStage('Creating personalized script...');
      setGenerationProgress(30);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/meditation/generate-personalized`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession.access_token}`
        },
        body: JSON.stringify({
          category,
          duration,
          backgroundSound,
          userProfile: userProfile?.quiz_answers || {},
          personalizedPlan: userProfile?.current_personalized_plan_id,
          customInput: customInput.trim(), // Include user's custom input
          jobId // Include job ID for tracking
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error === 'Insufficient tokens') {
          setError(
            <span>
              You don't have enough tokens to generate this meditation. 
              Required: {errorData.required}, Available: {errorData.available}. 
              <Link to="/get-tokens" style={{ color: '#1976d2', textDecoration: 'underline', marginLeft: '4px' }}>
                Get more tokens
              </Link>
            </span>
          );
          setGenerating(false);
          setGenerationProgress(0);
          setGenerationStage('');
          localStorage.removeItem('ongoingMeditation');
          // Auto-redirect after showing message
          setTimeout(() => navigate('/get-tokens'), 3000);
          return;
        }
        throw new Error(errorData.error || 'Failed to generate meditation');
      }

      setGenerationStage('Generating audio with AI voice...');
      setGenerationProgress(70);

      const data = await response.json();
      
      setGenerationStage('Adding background sounds...');
      setGenerationProgress(90);

      await new Promise(resolve => setTimeout(resolve, 1000));

      setGenerationStage('Finalizing your meditation...');
      setGenerationProgress(100);

      await new Promise(resolve => setTimeout(resolve, 500));

      // Handle completion
      handleGenerationComplete({
        ...data.meditation,
        tokensRemaining: data.tokensRemaining
      });

    } catch (error) {
      setError('Failed to generate personalized meditation. Please try again.');
      console.error('Error generating meditation:', error);
      setGenerating(false);
      setGenerationProgress(0);
      setGenerationStage('');
      localStorage.removeItem('ongoingMeditation');
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      clearInterval(intervalRef.current);
    } else {
      audioRef.current.play();
      intervalRef.current = setInterval(() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
      }, 1000);
    }
    setIsPlaying(!isPlaying);
  };

  const handleStop = () => {
    if (!audioRef.current) return;
    
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setCurrentTime(0);
    setIsPlaying(false);
    clearInterval(intervalRef.current);
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue / 100);
    if (audioRef.current) {
      audioRef.current.volume = newValue / 100;
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleMeditationSelect = (meditation) => {
    setSelectedMeditation(meditation);
    setCurrentTime(0);
    setIsPlaying(false);
    
    if (meditation.audio_url) {
      setAudioUrl(meditation.audio_url);
    }
  };

  const deleteMeditation = async (meditationId, meditationTitle, isPersonalized = false) => {
    // Show confirmation dialog
    const confirmed = window.confirm(`Are you sure you want to delete "${meditationTitle}"? This action cannot be undone.`);
    
    if (!confirmed) return;

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        setError('Please log in to delete meditations');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/meditation/${meditationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete meditation');
      }

      // Remove from local state
      if (isPersonalized) {
        setPersonalizedMeditations(prev => prev.filter(m => m.id !== meditationId));
      }

      // Clear selection if the deleted meditation was selected
      if (selectedMeditation?.id === meditationId) {
        setSelectedMeditation(null);
        setAudioUrl('');
        setIsPlaying(false);
      }

      // Show success message (optional)
      console.log('Meditation deleted successfully');

    } catch (error) {
      setError('Failed to delete meditation. Please try again.');
      console.error('Error deleting meditation:', error);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Menu handlers
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleMenuClose();
    navigate(path);
  };

  const handleLogoutFromMenu = async () => {
    handleMenuClose();
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: '#fafbfc', 
      minHeight: '100vh',
      '& @keyframes pulse': {
        '0%': { opacity: 1, transform: 'scale(1)' },
        '50%': { opacity: 0.7, transform: 'scale(1.1)' },
        '100%': { opacity: 1, transform: 'scale(1)' }
      },
      '& @keyframes glow': {
        '0%': { 
          boxShadow: '0 0 20px rgba(255, 255, 255, 0.2)',
          transform: 'scale(1)'
        },
        '100%': { 
          boxShadow: '0 0 30px rgba(255, 255, 255, 0.4), 0 0 40px rgba(255, 255, 255, 0.1)',
          transform: 'scale(1.05)'
        }
      }
    }}>
      {/* Progress Dashboard Style Header */}
      <Box sx={{ 
        bgcolor: 'white', 
        borderBottom: '1px solid #e0e4e7',
        py: { xs: 2, sm: 3 }
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between"
               flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }}>
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}
                 flexDirection={{ xs: 'column', sm: 'row' }} textAlign={{ xs: 'center', sm: 'left' }}>
              <IconButton 
                onClick={() => navigate('/conversation')}
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 }
                }}
              >
                <ArrowBack sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
              
              <Box>
                <Typography variant="h4" fontWeight="600" color="#1a1a1a"
                           fontSize={{ xs: '1.5rem', sm: '2rem', md: '2.125rem' }}>
                  Intelligent Meditation 🧘‍♂️
                </Typography>
                <Typography variant="body1" color="#6b7280" sx={{ mt: 0.5 }}
                           fontSize={{ xs: '0.875rem', sm: '1rem' }}>
                  Personalized, AI-powered meditation sessions for your wellness journey
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                onClick={() => navigate('/challenges')}
                startIcon={<EmojiEvents />}
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    borderColor: '#5a67d8',
                    backgroundColor: 'rgba(102, 126, 234, 0.05)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                  },
                  transition: 'all 0.2s ease',
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                Start Challenge
              </Button>
              
              {/* Mobile Challenge Button */}
              <IconButton
                onClick={() => navigate('/challenges')}
                sx={{
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  display: { xs: 'flex', sm: 'none' },
                  '&:hover': { 
                    bgcolor: 'rgba(102, 126, 234, 0.2)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease',
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 }
                }}
                title="Start Challenge"
              >
                <EmojiEvents sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
              
              <Badge badgeContent={personalizedMeditations.length} color="primary">
                <IconButton 
                  onClick={handleMenuOpen}
                  sx={{ 
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                    '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 }
                  }}
                >
                  <MoreVert sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              </Badge>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          
          {/* Gamified Stats Row */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: { xs: 3, sm: 4 },
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
              border: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': { 
                transform: 'translateY(-8px) scale(1.02)',
                boxShadow: '0 16px 48px rgba(102, 126, 234, 0.4)'
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                zIndex: 1
              }
            }}>
              <CardContent sx={{ 
                p: { xs: 3, sm: 4 }, 
                position: 'relative', 
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
                  <Box sx={{ 
                    width: { xs: 56, sm: 64 }, 
                    height: { xs: 56, sm: 64 }, 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.25)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    animation: 'glow 2s ease-in-out infinite alternate',
                    animationDelay: '0s'
                  }}>
                    <SelfImprovement sx={{ fontSize: { xs: 28, sm: 32 } }} />
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography 
                    variant="h2" 
                    fontWeight="800" 
                    color="white"
                    sx={{
                      fontSize: { xs: '2.5rem', sm: '3.5rem' },
                      textShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      mb: 1
                    }}
                  >
                    {personalizedMeditations.length}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    fontWeight="600" 
                    color="rgba(255,255,255,0.95)"
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 0.5 }}
                  >
                    Meditations
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="rgba(255,255,255,0.8)"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Your collection 🎧
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              borderRadius: { xs: 3, sm: 4 },
              boxShadow: '0 8px 32px rgba(79, 172, 254, 0.3)',
              border: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': { 
                transform: 'translateY(-8px) scale(1.02)',
                boxShadow: '0 16px 48px rgba(79, 172, 254, 0.4)'
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                zIndex: 1
              }
            }}>
              <CardContent sx={{ 
                p: { xs: 3, sm: 4 }, 
                position: 'relative', 
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
                  <Box sx={{ 
                    width: { xs: 56, sm: 64 }, 
                    height: { xs: 56, sm: 64 }, 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.25)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    animation: 'glow 2s ease-in-out infinite alternate',
                    animationDelay: '0.7s'
                  }}>
                    <TrendingUp sx={{ fontSize: { xs: 28, sm: 32 } }} />
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography 
                    variant="h2" 
                    fontWeight="800" 
                    color="white"
                    sx={{
                      fontSize: { xs: '2.5rem', sm: '3.5rem' },
                      textShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      mb: 1
                    }}
                  >
                    {Math.max(1, Math.floor(personalizedMeditations.reduce((total, m) => total + (m.duration || 1), 0) / 7))}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    fontWeight="600" 
                    color="rgba(255,255,255,0.95)"
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 0.5 }}
                  >
                    Week Streak
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="rgba(255,255,255,0.8)"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Keep going! 🔥
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              borderRadius: { xs: 3, sm: 4 },
              boxShadow: '0 8px 32px rgba(240, 147, 251, 0.3)',
              border: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': { 
                transform: 'translateY(-8px) scale(1.02)',
                boxShadow: '0 16px 48px rgba(240, 147, 251, 0.4)'
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                zIndex: 1
              }
            }}>
              <CardContent sx={{ 
                p: { xs: 3, sm: 4 }, 
                position: 'relative', 
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
                  <Box sx={{ 
                    width: { xs: 56, sm: 64 }, 
                    height: { xs: 56, sm: 64 }, 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.25)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    animation: 'glow 2s ease-in-out infinite alternate',
                    animationDelay: '1.4s'
                  }}>
                    <Timer sx={{ fontSize: { xs: 28, sm: 32 } }} />
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography 
                    variant="h2" 
                    fontWeight="800" 
                    color="white"
                    sx={{
                      fontSize: { xs: '2.5rem', sm: '3.5rem' },
                      textShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      mb: 1
                    }}
                  >
                    {personalizedMeditations.reduce((total, m) => total + (m.duration || 1), 0)}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    fontWeight="600" 
                    color="rgba(255,255,255,0.95)"
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 0.5 }}
                  >
                    Total Minutes
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="rgba(255,255,255,0.8)"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Time invested 🕐
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12}>
            <Card sx={{ 
              bgcolor: 'white',
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e0e4e7',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h5" fontWeight="700" color="#1a1a1a" gutterBottom
                           fontSize={{ xs: '1.1rem', sm: '1.5rem' }}>
                  Create Your Perfect Meditation ✨
                </Typography>
            
            {/* Quick Generate Section */}
            <Card sx={{ 
              mb: { xs: 2, sm: 4 }, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: { xs: 2, sm: 3 }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  gap: { xs: 1, sm: 2 }
                }}>
                  <Psychology sx={{ color: 'white', fontSize: { xs: 24, sm: 32 } }} />
                  <Typography variant="h6" sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.125rem', sm: '1.25rem' }
                  }}>
                    Generate Your Personalized Meditation
                  </Typography>
                </Box>
                
                {/* Caption */}
                <Typography variant="body2" sx={{ 
                  color: 'rgba(255,255,255,0.8)', 
                  mb: 3,
                  textAlign: 'center',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}>
                  5 mins or more suggested for best results
                </Typography>
                
                {/* Custom Input Field */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value)}
                    placeholder="What are you struggling with? What do you want help with? What would you like to accomplish? (Optional)"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255,255,255,0.5)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255,255,255,0.7)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'white',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'rgba(255,255,255,0.7)',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
                
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl 
                      fullWidth 
                      variant="outlined"
                      sx={{ 
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'white' }
                      }}
                    >
                      <InputLabel sx={{ color: 'rgba(255,255,255,0.8)', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Category
                      </InputLabel>
                      <Select
                        value={meditationType}
                        label="Category"
                        onChange={(e) => setMeditationType(e.target.value)}
                        sx={{ 
                          color: 'white', 
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                          '& .MuiSvgIcon-root': { color: 'white' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                      >
                        {meditationCategories.map(cat => (
                          <MenuItem key={cat.id} value={cat.id}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {cat.icon} {cat.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {cat.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography sx={{ color: 'white', mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Duration: 5 minutes (25 tokens)
                      </Typography>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          borderRadius: 2,
                          border: '1px solid rgba(255,255,255,0.2)',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                          5 min
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Perfect duration
                        </Typography>
                      </Paper>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl 
                      fullWidth 
                      variant="outlined"
                      sx={{ 
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'white' }
                      }}
                    >
                      <InputLabel sx={{ color: 'rgba(255,255,255,0.8)', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Background Sound
                      </InputLabel>
                      <Select
                        value={backgroundSound}
                        label="Background Sound"
                        onChange={(e) => setBackgroundSound(e.target.value)}
                        sx={{ 
                          color: 'white', 
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                          '& .MuiSvgIcon-root': { color: 'white' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                      >
                        {backgroundSounds.map(sound => (
                          <MenuItem key={sound.id} value={sound.id}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {sound.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {sound.mood}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={12} md={3}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={handleGenerateClick}
                      disabled={generating}
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        pointerEvents: generating ? 'none' : 'auto',
                        '&:hover': { 
                          bgcolor: 'rgba(255,255,255,0.3)',
                          border: '1px solid rgba(255,255,255,0.5)'
                        },
                        '&:disabled': {
                          bgcolor: 'rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.5)',
                          pointerEvents: 'none'
                        },
                        py: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      {generating ? (
                        <>
                          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                          Generating...
                        </>
                      ) : (
                        'Generate Meditation (25 tokens)'
                      )}
                    </Button>
                  </Grid>
                </Grid>

                {/* Generation Progress */}
                {generating && (
                  <Box sx={{ mt: 3, p: 3, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.2)' }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                      Creating Your Personalized Meditation
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                          {generationStage || 'Initializing...'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          {Math.round(generationProgress)}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={generationProgress} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'linear-gradient(90deg, #4CAF50, #81C784)',
                            borderRadius: 4
                          }
                        }} 
                      />
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>

            {error && (
              <Alert severity="error" sx={{ mb: { xs: 3, sm: 4 } }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Generated Meditations */}
            {personalizedMeditations.length > 0 && (
              <Box id="generated-meditations">
                <Typography variant="h5" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontWeight: 'bold',
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}>
                  <Star sx={{ color: '#FFD700' }} /> Your Personalized Meditations
                </Typography>
                
                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
                  {personalizedMeditations.map((meditation) => (
                    <Grid item xs={12} sm={6} md={4} key={meditation.id}>
                      <Card 
                        sx={{ 
                          borderRadius: 3,
                          transition: 'all 0.3s ease',
                          '&:hover': { 
                            transform: 'translateY(-4px)',
                            boxShadow: 6
                          },
                          border: '1px solid rgba(0,0,0,0.1)',
                          overflow: 'hidden'
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          {/* Header with title and delete button */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="h6" component="h3" sx={{ 
                              flex: 1, 
                              fontWeight: 'bold',
                              fontSize: { xs: '1rem', sm: '1.125rem' }
                            }}>
                              {meditation.title}
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => deleteMeditation(meditation.id, meditation.title, true)}
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { 
                                  backgroundColor: 'error.light', 
                                  color: 'white' 
                                }
                              }}
                              title="Delete meditation"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>

                          {/* Description */}
                          <Typography color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
                            {meditation.description}
                          </Typography>

                          {/* Meditation info chips */}
                          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            <Chip 
                              label={`${meditation.duration} min`} 
                              size="small" 
                              icon={<AccessTime />}
                              sx={{ fontSize: '0.75rem' }}
                            />
                            <Chip 
                              label={meditationCategories.find(c => c.id === meditation.category)?.name || meditation.category}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                            {meditation.background_sound && meditation.background_sound !== 'silence' && (
                              <Chip 
                                size="small" 
                                label={backgroundSounds.find(s => s.id === meditation.background_sound)?.name?.split(' ')[0] || 'Music'}
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            )}
                          </Box>

                          {/* Audio Controls */}
                          {meditation.audio_url ? (
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'grey.50', 
                              borderRadius: 2,
                              border: '1px solid rgba(0,0,0,0.05)'
                            }}>
                              {/* Audio element for this specific meditation */}
                              <audio
                                ref={(el) => {
                                  if (el && selectedMeditation?.id === meditation.id) {
                                    audioRef.current = el;
                                  }
                                }}
                                src={meditation.audio_url}
                                onTimeUpdate={selectedMeditation?.id === meditation.id ? handleTimeUpdate : undefined}
                                onLoadedMetadata={() => {
                                  if (selectedMeditation?.id === meditation.id && audioRef.current) {
                                    setDuration(audioRef.current.duration);
                                  }
                                }}
                                onEnded={() => {
                                  if (selectedMeditation?.id === meditation.id) {
                                    setIsPlaying(false);
                                    setCurrentTime(0);
                                    // Notify backend of completed meditation session
                                    if (session) {
                                      notifyBackendOfMeditationActivity(session, showChallengeCompletion);
                                    }
                                  }
                                }}
                              />

                              {/* Play/Pause Controls */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <IconButton
                                  onClick={() => {
                                    if (selectedMeditation?.id === meditation.id && isPlaying) {
                                      // Pause current meditation
                                      setIsPlaying(false);
                                      if (audioRef.current) audioRef.current.pause();
                                    } else {
                                      // Play this meditation
                                      setSelectedMeditation(meditation);
                                      setAudioUrl(meditation.audio_url);
                                      setTimeout(() => {
                                        if (audioRef.current) {
                                          audioRef.current.play();
                                          setIsPlaying(true);
                                        }
                                      }, 100);
                                    }
                                  }}
                                  size="large"
                                  sx={{ 
                                    bgcolor: selectedMeditation?.id === meditation.id && isPlaying ? 'primary.main' : 'grey.300',
                                    color: selectedMeditation?.id === meditation.id && isPlaying ? 'white' : 'grey.700',
                                    '&:hover': { 
                                      bgcolor: selectedMeditation?.id === meditation.id && isPlaying ? 'primary.dark' : 'grey.400'
                                    }
                                  }}
                                >
                                  {selectedMeditation?.id === meditation.id && isPlaying ? 
                                    <Pause /> : 
                                    <PlayArrow />
                                  }
                                </IconButton>
                                
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                    {selectedMeditation?.id === meditation.id ? 
                                      `${formatTime(currentTime)} / ${formatTime(duration)}` :
                                      'Ready to play'
                                    }
                                  </Typography>
                                  {selectedMeditation?.id === meditation.id && (
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={progress} 
                                      sx={{ height: 4, borderRadius: 2 }}
                                    />
                                  )}
                                </Box>
                              </Box>

                              {/* Volume Control - only show for currently playing meditation */}
                              {selectedMeditation?.id === meditation.id && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <VolumeDown sx={{ fontSize: 16 }} />
                                  <Slider
                                    value={volume}
                                    onChange={(_, newValue) => {
                                      setVolume(newValue);
                                      if (audioRef.current) {
                                        audioRef.current.volume = newValue / 100;
                                      }
                                    }}
                                    size="small"
                                    sx={{ flexGrow: 1, mx: 1 }}
                                  />
                                  <VolumeUp sx={{ fontSize: 16 }} />
                                </Box>
                              )}

                              {/* Background sound info */}
                              <Typography variant="caption" color="text.secondary" sx={{ 
                                display: 'block', 
                                textAlign: 'center', 
                                mt: 1,
                                fontSize: '0.7rem'
                              }}>
                                {meditation.background_sound !== 'silence' ? 
                                  `🎵 With ${backgroundSounds.find(s => s.id === meditation.background_sound)?.name || 'background sound'}` :
                                  '🔇 Voice only'
                                }
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'warning.light', 
                              borderRadius: 2,
                              textAlign: 'center'
                            }}>
                              <Typography variant="body2" color="warning.dark">
                                Audio generation in progress...
                              </Typography>
                            </Box>
                          )}

                          {/* Date created */}
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            display: 'block', 
                            textAlign: 'right', 
                            mt: 2,
                            fontSize: '0.7rem'
                          }}>
                            Created {new Date(meditation.created_at).toLocaleDateString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    {/* Menu */}
    <Menu anchorEl={menuAnchorEl} open={isMenuOpen} onClose={handleMenuClose}>
      {/* Dopamine-producing links at the top */}
      <MenuItem onClick={() => handleMenuItemClick('/get-tokens')}>
        <ListItemIcon><MonetizationOn sx={{ color: '#FFD700' }} /></ListItemIcon>
        <ListItemText primary="Get Tokens" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/human-therapists')}>
        <ListItemIcon><People sx={{ color: '#4CAF50' }} /></ListItemIcon>
        <ListItemText primary="Human Therapists" />
      </MenuItem>
      <Divider />
      
      {/* Core features */}
      <MenuItem onClick={() => handleMenuItemClick('/conversation')}>
        <ListItemIcon><Psychology sx={{ color: '#E91E63' }} /></ListItemIcon>
        <ListItemText primary="Chat" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/journal')}>
        <ListItemIcon><BookOutlined sx={{ color: '#2196F3' }} /></ListItemIcon>
        <ListItemText primary="Journal" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/instant-wellness')}>
        <ListItemIcon><FlashOn sx={{ color: '#FF9800' }} /></ListItemIcon>
        <ListItemText primary="Instant Wellness" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/intelligent-meditation')}>
        <ListItemIcon><Psychology sx={{ color: '#9C27B0' }} /></ListItemIcon>
        <ListItemText primary="Meditation" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/progress')}> 
        <ListItemIcon><Analytics sx={{ color: '#00BCD4' }} /></ListItemIcon>
        <ListItemText primary="Progress" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/challenges')}>
        <ListItemIcon><SportsEsports sx={{ color: '#FF5722' }} /></ListItemIcon>
        <ListItemText primary="Challenges" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/mind-games')}>
        <ListItemIcon><EmojiObjects sx={{ color: '#9C27B0' }} /></ListItemIcon>
        <ListItemText primary="Mind Games" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/leaderboard')}>
        <ListItemIcon><EmojiEvents sx={{ color: '#FFC107' }} /></ListItemIcon>
        <ListItemText primary="Leaderboard" />
      </MenuItem>
      <MenuItem onClick={() => handleMenuItemClick('/conversation')}>
        <ListItemIcon><Settings sx={{ color: '#757575' }} /></ListItemIcon>
        <ListItemText primary="Settings" />
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogoutFromMenu}>
        <ListItemIcon><Logout sx={{ color: '#F44336' }} /></ListItemIcon>
        <ListItemText primary="Logout" />
      </MenuItem>
    </Menu>
  </Container>
</Box>
  );
};

export default IntelligentMeditation;
